import React from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  Trash2,
  ShieldCheck,
  Download,
  Plus,
  MapPin,
  BarChart3,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KINDS = [
  "free_parking",
  "paid_parking",
  "car_park",
  "street_parking",
  "accessible_bay",
  "motorcycle_parking",
  "residents_only",
  "permit_only",
  "no_parking",
  "loading_bay",
  "time_limited",
  "other_restriction",
];
const STATUSES = ["published", "pending", "hidden", "rejected"];
const MALTA_BBOX = { min_lat: 35.78, min_lng: 14.18, max_lat: 36.1, max_lng: 14.58 };

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Malta" });
  } catch {
    return iso;
  }
};

/* ===== Parking data tab (verified free parking + restrictions + OSM import) ===== */
export function ParkingDataTab({ adminKey }) {
  const [items, setItems] = React.useState([]);
  const [counts, setCounts] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState("published");
  const [kind, setKind] = React.useState("");
  const [importing, setImporting] = React.useState(false);
  const [importResult, setImportResult] = React.useState(null);
  const [form, setForm] = React.useState({ kind: "free_parking", name: "", lat: "", lng: "", notes: "", schedule: "" });
  const [creating, setCreating] = React.useState(false);

  const headers = React.useMemo(() => ({ "X-Admin-Key": adminKey }), [adminKey]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/parking-places`, {
        headers,
        params: { status: status || undefined, kind: kind || undefined, limit: 200 },
      });
      setItems(data.places || []);
      setCounts(data.counts_by_status || {});
    } catch {
      toast.error("Failed to load parking data.");
    } finally {
      setLoading(false);
    }
  }, [headers, status, kind]);
  React.useEffect(() => { load(); }, [load]);

  const runImport = async (dry) => {
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/parking-places/import/osm`,
        { ...MALTA_BBOX, dry_run: dry },
        { headers: { ...headers, "Content-Type": "application/json" } }
      );
      setImportResult(data);
      toast.success(dry ? "Preview ready — nothing was written." : `Imported: ${data.created} new, ${data.updated} updated.`);
      if (!dry) load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OSM import failed.");
    } finally {
      setImporting(false);
    }
  };

  const verify = async (id) => {
    try {
      await axios.post(`${API}/admin/parking-places/${id}/verify`, {}, { headers });
      toast.success("Marked as verified and published.");
      load();
    } catch {
      toast.error("Could not verify.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await axios.delete(`${API}/admin/parking-places/${id}`, { headers });
      setItems((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const setStatusOf = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/parking-places/${id}`, { status: newStatus }, { headers: { ...headers, "Content-Type": "application/json" } });
      toast.success(`Status: ${newStatus}`);
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const create = async () => {
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast.error("Enter valid coordinates.");
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/parking-places`,
        { kind: form.kind, name: form.name || null, lat, lng, notes: form.notes || null, schedule: form.schedule || null },
        { headers: { ...headers, "Content-Type": "application/json" } }
      );
      toast.success("Verified location added.");
      setForm({ kind: "free_parking", name: "", lat: "", lng: "", notes: "", schedule: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add location.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-parking-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STATUSES.map((s) => (
          <div key={s} className="p-4 rounded-2xl bg-[#141416] border border-white/10">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{s}</div>
            <div className="mt-2 font-heading text-2xl font-bold text-white" data-testid={`parking-count-${s}`}>
              {counts[s] ?? 0}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 rounded-2xl bg-[#141416] border border-white/10">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
          OpenStreetMap import (free, no API key)
        </div>
        <p className="mt-1 text-[12px] text-zinc-400 leading-snug">
          Imports public parking for Malta &amp; Gozo. Imported records are never marked as verified;
          restrictions and thin street-side data stay <span className="text-zinc-200">pending</span> and hidden
          from users until you verify them.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button onClick={() => runImport(true)} disabled={importing} variant="outline" className="h-10 border-white/10 text-zinc-200 hover:bg-white/5" data-testid="osm-preview">
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Preview
          </Button>
          <Button onClick={() => runImport(false)} disabled={importing} className="h-10 bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold" data-testid="osm-import">
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Import now
          </Button>
        </div>
        {importResult && (
          <div className="mt-3 text-[12px] text-zinc-300 font-mono" data-testid="osm-import-result">
            {Object.entries(importResult).map(([k, v]) => (
              <span key={k} className="mr-3">{k}: <span className="text-[#ccff00]">{String(v)}</span></span>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 rounded-2xl bg-[#141416] border border-white/10">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Add a verified location</div>
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          <select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="h-11 rounded-md bg-[#0a0a0c] border border-white/10 text-white px-3 text-sm"
            data-testid="parking-new-kind"
          >
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (optional)" className="h-11 bg-[#0a0a0c] border-white/10 text-white" data-testid="parking-new-name" />
          <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="Hours e.g. Mo-Fr 07:00-19:00" className="h-11 bg-[#0a0a0c] border-white/10 text-white" data-testid="parking-new-schedule" />
          <Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="Latitude" className="h-11 bg-[#0a0a0c] border-white/10 text-white" data-testid="parking-new-lat" />
          <Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="Longitude" className="h-11 bg-[#0a0a0c] border-white/10 text-white" data-testid="parking-new-lng" />
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="h-11 bg-[#0a0a0c] border-white/10 text-white" data-testid="parking-new-notes" />
        </div>
        <Button onClick={create} disabled={creating} className="mt-3 h-10 bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold" data-testid="parking-new-save">
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Add verified location
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md bg-[#141416] border border-white/10 text-zinc-200 px-2 text-xs" data-testid="parking-filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-md bg-[#141416] border border-white/10 text-zinc-200 px-2 text-xs" data-testid="parking-filter-kind">
          <option value="">All kinds</option>
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <Button onClick={load} size="sm" variant="ghost" className="ml-auto h-8 text-zinc-300" data-testid="parking-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-zinc-500" data-testid="parking-empty">No locations match these filters.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((p) => (
              <div key={p.id} className="p-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px]" data-testid={`parking-row-${p.id}`}>
                <span className="font-semibold text-white">{p.name || p.kind}</span>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-zinc-300">{p.kind}</span>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  p.confidence === "confirmed" ? "bg-[#ccff00]/10 text-[#ccff00]" : p.confidence === "reported" ? "bg-amber-500/10 text-amber-200" : "bg-[#22d3ee]/10 text-[#67e8f9]"
                }`}>{p.confidence}</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">{p.status}</span>
                <span className="text-zinc-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.lat.toFixed(5)}, {p.lng.toFixed(5)}</span>
                {p.source_url && (
                  <a href={p.source_url} target="_blank" rel="noreferrer" className="text-[#67e8f9] underline">{p.source_ref}</a>
                )}
                <span className="text-zinc-600">{fmt(p.created_at)}</span>
                <div className="ml-auto flex items-center gap-2">
                  {p.confidence !== "confirmed" && (
                    <button onClick={() => verify(p.id)} className="h-8 px-2.5 rounded-lg bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] font-semibold inline-flex items-center gap-1.5" data-testid={`parking-verify-${p.id}`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Verify
                    </button>
                  )}
                  <select
                    value={p.status}
                    onChange={(e) => setStatusOf(p.id, e.target.value)}
                    className="h-8 rounded-md bg-[#0a0a0c] border border-white/10 text-zinc-200 px-1.5 text-[11px]"
                    data-testid={`parking-status-${p.id}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(p.id)} className="h-8 px-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 font-semibold inline-flex items-center gap-1.5" data-testid={`parking-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-[11px] text-zinc-600">
        Data sources: OpenStreetMap (imported, free) and SpotDrop admin verification. Restrictions are only
        visible to users once verified.
      </p>
    </div>
  );
}

/* ===== Analytics tab ===== */
const RANGES = [
  { k: "1", label: "24 h" },
  { k: "7", label: "7 days" },
  { k: "30", label: "30 days" },
  { k: "all", label: "All" },
];

export function AnalyticsTab({ adminKey }) {
  const [range, setRange] = React.useState("7");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`, {
        headers: { "X-Admin-Key": adminKey },
        params: { range },
      });
      setData(res.data);
    } catch {
      toast.error("Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [adminKey, range]);
  React.useEffect(() => { load(); }, [load]);

  const max = Math.max(1, ...((data?.events || []).map((e) => e.count)));

  return (
    <div data-testid="admin-analytics-tab">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.k}
              type="button"
              onClick={() => setRange(r.k)}
              className={`h-8 px-3 rounded-full text-[11px] font-medium ${range === r.k ? "bg-[#ccff00] text-[#0a0a0c]" : "bg-white/5 text-zinc-400"}`}
              data-testid={`analytics-range-${r.k}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Button onClick={load} className="h-8 px-3 bg-white/5 border border-white/10 text-xs" data-testid="analytics-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="text-[11px] text-zinc-500 mb-2" data-testid="analytics-total">
        {data?.total_events ?? 0} events recorded
      </div>

      {(data?.events || []).length === 0 ? (
        <div className="p-10 text-center text-zinc-500 rounded-2xl bg-[#141416] border border-white/10" data-testid="analytics-empty">
          <BarChart3 className="w-5 h-5 mx-auto mb-2 opacity-50" />
          No events yet for this period.
        </div>
      ) : (
        <div className="space-y-1.5">
          {data.events.map((e) => (
            <div key={e.event} className="p-3 rounded-xl bg-white/[0.03] border border-white/10" data-testid={`analytics-event-${e.event}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="text-[13px] text-white">{e.event}</div>
                <div className="text-[13px] font-mono text-[#ccff00]">{e.count}</div>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-[#ccff00]/70" style={{ width: `${(100 * e.count) / max}%` }} />
              </div>
              <div className="mt-1 text-[10px] text-zinc-500">{e.devices} device{e.devices === 1 ? "" : "s"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
